import { Tooltip as ReactTooltip, PlacesType } from 'react-tooltip';
import { FC, ReactElement, ReactNode, cloneElement, useState } from 'react';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import styles from './tooltip.module.scss';

const circleInfoIcon = getDesignSystemIcon('icon-circle-info.svg');

export interface TooltipProps {
  title?: string;
  body: string | ReactNode;
  id: string;
  position?: PlacesType;
  maxWidth?: number;
  icon?: ReactElement;
  iconClassName?: string;
}

export const Tooltip: FC<React.PropsWithChildren<TooltipProps>> = ({
  title,
  body,
  id,
  maxWidth = 12.5,
  position = 'top',
  icon = (
    <svg>
      <use xlinkHref={`${circleInfoIcon}#img`} />
    </svg>
  ),
  iconClassName = 'inline-block h-4 w-4 shrink-0 text-primary-black',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-tooltip-id={id}
        className="focus-outline w-fit"
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setIsOpen(false)}
        aria-label={title}
        aria-describedby={isOpen ? `tooltip-${id}` : undefined}
        aria-expanded={isOpen}
      >
        {cloneElement(icon, { id, className: iconClassName })}
      </button>
      <ReactTooltip id={id} className={styles.tooltip} place={position} noArrow={true} isOpen={isOpen}>
        <div className="whitespace-normal" style={{ maxWidth: `${maxWidth}rem` }}>
          {title && <h5 className="stihl-text-body-sm-bold">{title}</h5>}
          <p className="stihl-text-body-sm-reg">{body}</p>
        </div>
      </ReactTooltip>
    </>
  );
};
